export const isFieldRequired = (field, parentSchema) => {
    // Navigate schema to the appropriate level
    if (!parentSchema?.required) return false;

    return parentSchema.required.includes(field);
};

export function getTitleFromProperties(schema, field) {
    if (!schema?.properties) return null;
    const fieldSchema = schema.properties[field];
    return fieldSchema?.title || null;
}

export function getTitleFromDefs(schema, field) {
    if (!schema?.$defs) return null;
    const fieldSchema = schema.$defs[field];
    return fieldSchema?.title || null;
}

export function getTitleByField(schema, field) {
    // First, check in properties
    let title = getTitleFromProperties(schema, field);
    if (title) return title;

    // Then, check in $defs
    title = getTitleFromDefs(schema, field);
    if (title) return title;

    // If the field is a nested property (like $defs.Address.properties.street_address)
    const [defName, nestedField] = field.split('.');

    // Check if it's a defined reference in $defs and get nested properties
    const defSchema = schema?.$defs?.[defName];
    if (defSchema?.properties) {
        return defSchema.properties[nestedField]?.title || null;
    }

    // Return null if no title found
    return null;
}


export function getDescriptionFromProperties(schema, field) {
    if (!schema?.properties) return null;
    const fieldSchema = schema.properties[field];
    return fieldSchema?.description || null;
}

export function getDescriptionFromDefs(schema, field) {
    if (!schema?.$defs) return null;
    const fieldSchema = schema.$defs[field];
    return fieldSchema?.description || null;
}

export function getDescriptionByField(schema, field) {
    // First, check in properties
    let description = getDescriptionFromProperties(schema, field);
    if (description) return description;

    // Then, check in $defs
    description = getDescriptionFromDefs(schema, field);
    if (description) return description;

    // If the field is a nested property (like $defs.Address.properties.street_address)
    const [defName, nestedField] = field.split('.');

    // Check if it's a defined reference in $defs and get nested properties
    const defSchema = schema?.$defs?.[defName];
    if (defSchema?.properties) {
        return defSchema.properties[nestedField]?.description || null;
    }

    // Return null if no description found
    return null;
}

export function parseErrorData(errorData) {
  let parsedErrors = [];
  // Case 1: If errorData is a simple key-value pair
  if (typeof errorData === "object" && !Array.isArray(errorData)) {
    Object.keys(errorData).forEach((key) => {
      const errorValue = errorData[key];

      // Case 1a: If the value is a simple string (like {"detail": "Error message"})
      if (typeof errorValue === "string") {
        parsedErrors.push(`${key}: ${errorValue}`);
      }
      // Case 1b: If the value is an array of strings (flat error)
      else if (Array.isArray(errorValue) && typeof errorValue[0] === "string") {
        parsedErrors.push(`${key}: ${errorValue[0]}`);
      }
      // Case 1c: If the value is an array of objects (nested errors)
      else if (Array.isArray(errorValue) && typeof errorValue[0] === "object") {
        errorValue.forEach((nestedError) => {
          Object.keys(nestedError).forEach((nestedKey) => {
            parsedErrors.push(
              `${key} - ${nestedKey}: ${nestedError[nestedKey]}`
            );
          });
        });
      }
    });
  } else if (typeof errorData === "string") {
    // Handle case where the error itself is just a string
    parsedErrors.push(errorData);
  }
  return parsedErrors;
}

/* export function parseValidationErrors(errorObj) {
    let errorMessages = '';

    // Iterate over the error object
    for (const field in errorObj) {
      if (errorObj.hasOwnProperty(field)) {
        const fieldErrors = errorObj[field];

        // Check if the field errors are an object (nested errors like address)
        if (typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
          for (const subField in fieldErrors) {
            if (fieldErrors.hasOwnProperty(subField)) {
              const subFieldErrors = fieldErrors[subField];
              subFieldErrors.forEach((errorMessage) => {
                errorMessages += `${field}.${subField}: ${errorMessage}\n`;
              });
            }
          }
        } else if (Array.isArray(fieldErrors)) {
          // If it's an array (direct errors for the field)
          fieldErrors.forEach((errorMessage) => {
            errorMessages += `${field}: ${errorMessage}\n`;
          });
        }
      }
    }
    return errorMessages;
  } */
  // modify to handle Objects with indexed keys (email: { "0": [...] }) and Direct arrays (organization_type: [...])
  export function parseValidationErrors(errorObj) {
    let errorMessages = '';

    for (const field in errorObj) {
      if (errorObj.hasOwnProperty(field)) {
        const fieldErrors = errorObj[field];

        if (typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
          // Handle nested errors (e.g., address, legal_representative)
          for (const subField in fieldErrors) {
            if (fieldErrors.hasOwnProperty(subField)) {
              const subFieldErrors = fieldErrors[subField];

              if (Array.isArray(subFieldErrors)) {
                // Case: Direct array of errors (expected behavior)
                subFieldErrors.forEach((errorMessage) => {
                  errorMessages += `${field}.${subField}: ${errorMessage}\n`;
                });
              } else if (typeof subFieldErrors === "object") {
                // Case: Errors indexed by numbers (e.g., "0": ["Enter a valid email"])
                for (const index in subFieldErrors) {
                  if (subFieldErrors.hasOwnProperty(index) && Array.isArray(subFieldErrors[index])) {
                    subFieldErrors[index].forEach((errorMessage) => {
                      errorMessages += `${field}.${subField}[${index}]: ${errorMessage}\n`;
                    });
                  }
                }
              }
            }
          }
        } else if (Array.isArray(fieldErrors)) {
          // Case: Direct array of errors
          fieldErrors.forEach((errorMessage) => {
            errorMessages += `${field}: ${errorMessage}\n`;
          });
        }
      }
    }

    return errorMessages;
  }


  export function cleanEmptyFields(obj) {
    // Check if the value is an array or an object and recursively clean it
    if (Array.isArray(obj)) {
      return obj
        .map(item => cleanEmptyFields(item)) // Recursively clean each item in the array
        .filter(item => item !== undefined && item !== null && !(Array.isArray(item) && item.length === 0)); // Filter out empty items
    } else if (typeof obj === 'object' && obj !== null) {
      const cleanedObj = {};
      for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
          const cleanedValue = cleanEmptyFields(obj[key]);
          // Only add non-empty values to the final object
          if (
            cleanedValue !== undefined &&
            cleanedValue !== null &&
            (typeof cleanedValue !== 'object' || Object.keys(cleanedValue).length > 0) &&
            (!Array.isArray(cleanedValue) || cleanedValue.length > 0)
          ) {
            cleanedObj[key] = cleanedValue;
          }
        }
      }
      // Remove objects that ONLY contain an "id" field
      // if (Object.keys(cleanedObj).length === 1 && "id" in cleanedObj) {
      //   return undefined;
      // }
      return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined; // If object is empty, return undefined
    }
    // For primitive values, return only if they are not empty strings
    return obj !== "" ? obj : undefined;
  }

  export function replaceIdOnlyObjectsWithNull(obj) {
    if (Array.isArray(obj)) {
      return obj.map(item => replaceIdOnlyObjectsWithNull(item));
    } else if (typeof obj === "object" && obj !== null) {
      const newObj = {};
      for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
          const value = replaceIdOnlyObjectsWithNull(obj[key]);

          // If the object contains ONLY an "id" field, replace it with null
          if (typeof value === "object" && value !== null && Object.keys(value).length === 1 && "id" in value) {
            newObj[key] = null;
          } else {
            newObj[key] = value;
          }
        }
      }
      return newObj;
    }
    return obj;
  }


export const validateFormData = (formData, requiredFields) => {
  let errors = {};
  requiredFields.forEach((field) => {
    const value = formData[field];
    if (Array.isArray(value)) {
      // If it's a list, check if it's empty
      if (value.length === 0) {
        errors[field] = "This field is required";
      }
    } else if (typeof value === "string") {
      // If it's a string, check if it's empty or just spaces
      if (!value.trim()) {
        errors[field] = "This field is required";
      }
    } else if (typeof value === "object" && value !== null) {
      // If it's an object, recursively validate required subfields
      let nestedErrors = {};

      Object.keys(value).forEach((subfield) => {
        const subValue = value[subfield];
        if (Array.isArray(subValue) && subValue.length === 0) {
          nestedErrors[subfield] = "This field is required";
        } else if (typeof subValue === "string" && !subValue.trim()) {
          nestedErrors[subfield] = "This field is required";
        }
      });

      if (Object.keys(nestedErrors).length > 0) {
        errors[field] = nestedErrors;
      }
    }
  });

  return errors;
};